/**
 * @file ShopSidebarNav.tsx
 * @description Шапка и меню боковой панели кабинета магазина (phase19.31,
 * SC14–SC18, SC30; uiux-design §3.1, §3.3; замечания владельца по панели).
 *
 * Обычный кабинет: «Сводка», группы-аккордеон (открыта группа текущей
 * страницы, открытие другой сворачивает остальные), «Настройки» последним
 * пунктом. Поиск по меню (когда пунктов больше `NAV_SEARCH_MIN_ITEMS`)
 * закреплён в шапке панели под магазином и не прокручивается вместе со
 * списком; пока в нём есть текст — вместо групп все совпадения из всех
 * групп, после очистки — снова открыта группа текущей страницы.
 * Экран кладовщика: плоский список пунктов «Склада» без групп и поиска.
 * Что видно — решает `buildShopNav` по правам и роли; здесь только раскладка.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router";

import { useNavAccordion } from "../../hooks/useNavAccordion";
import {
  NAV_SEARCH_MIN_ITEMS,
  findSectionItem,
  groupOfItem,
  isSettingsHubItem,
  navItems,
  shopRelativePath,
  type NavCounters,
  type VisibleShopNav,
} from "../../lib/shop-nav";
import { AppShell } from "../ui/AppShell";
import { NavItemLink, ShopNavGroup } from "./ShopNavGroup";
import { ShopNavSearchField, ShopNavSearchResults } from "./ShopNavSearch";

interface Props {
  nav: VisibleShopNav;
  slug: string;
  counters: NavCounters;
  /** Шапка панели: магазин и переключатель. */
  identity: ReactNode;
}

export function ShopSidebarNav({ nav, slug, counters, identity }: Props) {
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const section = findSectionItem(nav, shopRelativePath(pathname, slug));
  const accordion = useNavAccordion(groupOfItem(nav, section), pathname);
  const { reset } = accordion;
  // Перешли по найденному пункту — поиск отработал, показываем группы снова.
  useEffect(() => setQuery(""), [pathname]);

  const withSearch = nav.mode === "full" && navItems(nav).length > NAV_SEARCH_MIN_ITEMS;
  const searching = withSearch && query.trim() !== "";

  function onQuery(value: string) {
    setQuery(value);
    // Поиск очищен — открыта группа текущей страницы, как до поиска.
    if (value.trim() === "") reset();
  }

  return (
    <>
      <AppShell.SidebarHeader>
        <div className="flex flex-col gap-3">
          {identity}
          {withSearch ? <ShopNavSearchField value={query} onChange={onQuery} /> : null}
        </div>
      </AppShell.SidebarHeader>
      <AppShell.SidebarNav>
        {nav.mode === "warehouse" ? (
          // Экран кладовщика: только «Склад», без групп и поиска (SC30).
          <div className="flex flex-col gap-1" data-testid="shop-nav-warehouse">
            {nav.groups.flatMap((g) => g.items).map((item) => (
              <NavItemLink key={item.id} item={item} slug={slug} counters={counters} />
            ))}
          </div>
        ) : searching ? (
          <ShopNavSearchResults nav={nav} query={query} slug={slug} counters={counters} />
        ) : (
          <div className="flex flex-col gap-1 pb-2" data-testid="shop-nav">
            {nav.home ? <NavItemLink item={nav.home} slug={slug} counters={counters} /> : null}
            {nav.groups.map((group) => (
              <ShopNavGroup
                key={group.id}
                group={group}
                slug={slug}
                counters={counters}
                open={accordion.isOpen(group.id)}
                onToggle={accordion.toggle}
              />
            ))}
            {nav.settings ? (
              <NavItemLink
                item={nav.settings}
                slug={slug}
                counters={counters}
                // Разделы «Настроек» живут под своими адресами — подсвечиваем пункт по разделу.
                forceActive={isSettingsHubItem(nav, section)}
              />
            ) : null}
          </div>
        )}
      </AppShell.SidebarNav>
    </>
  );
}
